import { useMemo, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Facebook, Twitter } from 'lucide-react';
import { useActualite, useActualites } from '../hooks/usePublicApi';
import { useTexts } from '../hooks/useTexts';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { ActualiteImage } from '../types/api';

const formatDate = (value: string) => {
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  } catch (error) {
    return value;
  }
};

const resolveImageUrl = (url?: string | null) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const base = import.meta.env.VITE_API_BASE_URL || '';
  const normalizedBase = base.endsWith('/api') ? base.replace(/\/api$/, '') : base;
  return `${normalizedBase}${url}`;
};

export default function ActualiteDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { getText } = useTexts();

  const { data: actualite, loading, error } = useActualite(slug ?? null);

  // Initialiser Fancybox pour la galerie d'images
  useEffect(() => {
    Fancybox.bind('[data-fancybox="actualite-gallery"]');

    return () => {
      Fancybox.unbind('[data-fancybox="actualite-gallery"]');
      Fancybox.close();
    };
  }, [actualite]);

  // Récupérer d'autres actualités pour les suggestions
  const { data: relatedNews } = useActualites({
    page: 1,
    ordering: '-date_publication',
  });

  const otherNews = useMemo(() => {
    if (!actualite) return [];
    return relatedNews
      .filter((item) => item.slug !== actualite.slug)
      .slice(0, 3);
  }, [actualite, relatedNews]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-16 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !actualite) {
    return (
      <div className="min-h-screen bg-gray-50 pt-32 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ErrorMessage
            error={
              error ||
              (() => {
                const text = getText('news.not_found');
                return text === 'news.not_found'
                  ? "L'actualité demandée est introuvable ou a été retirée."
                  : text;
              })()
            }
          />
          <div className="mt-6">
            <Link
              to="/news"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {(() => {
                const text = getText('news.back_to_list');
                return text === 'news.back_to_list' ? 'Retour aux actualités' : text;
              })()}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const imageUrl = resolveImageUrl(actualite.image_url_final);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = encodeURIComponent(`${actualite.titre}`);
  const shareUrl = encodeURIComponent(currentUrl);

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {(() => {
              const text = getText('news.back');
              return text === 'news.back' ? 'Retour' : text;
            })()}
          </button>
        </div>

        <article className="bg-white rounded-3xl shadow-sm overflow-hidden border border-gray-100">
          {imageUrl && (
            <a
              href={imageUrl}
              data-fancybox="actualite-gallery"
              data-caption={actualite.titre}
            >
              <img
                src={imageUrl}
                alt={actualite.titre}
                className="w-full h-96 object-cover cursor-pointer hover:opacity-90 transition-opacity"
              />
            </a>
          )}

          <div className="p-8 md:p-12">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-6">
              <span className="inline-flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(actualite.date_publication)}
              </span>
              {actualite.projet_nom && (
                <span className="inline-flex items-center font-medium text-blue-600">
                  {actualite.projet_nom}
                </span>
              )}
              {actualite.auteur_nom && (
                <span className="inline-flex items-center">
                  {(() => {
                    const label = getText('news.author');
                    const fallback = label === 'news.author' ? 'Par' : label;
                    return `${fallback} ${actualite.auteur_nom}`;
                  })()}
                </span>
              )}
            </div>

            <h1 className="text-4xl font-bold text-gray-900 mb-6 leading-tight">
              {actualite.titre}
            </h1>

            {actualite.resume && (
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                {actualite.resume}
              </p>
            )}

            <div
              className="prose prose-lg max-w-none text-gray-800 prose-headings:text-gray-900 prose-a:text-blue-600"
              dangerouslySetInnerHTML={{ __html: actualite.contenu.replace(/\n/g, '<br />') }}
            />

            {/* Images supplémentaires */}
            {actualite.images_supplementaires && actualite.images_supplementaires.length > 0 && (
              <div className="mt-10">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">
                  {(() => {
                    const text = getText('news.additional_images');
                    return text === 'news.additional_images' ? 'Galerie photos' : text;
                  })()}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {actualite.images_supplementaires
                    .sort((a: ActualiteImage, b: ActualiteImage) => (a.ordre_affichage || 0) - (b.ordre_affichage || 0))
                    .map((image: ActualiteImage) => {
                      const imageUrl = resolveImageUrl(image.image_url_final);
                      if (!imageUrl) return null;
                      return (
                        <a
                          key={image.id}
                          href={imageUrl}
                          data-fancybox="actualite-gallery"
                          data-caption={image.description || actualite.titre}
                          className="relative group block"
                        >
                          <img
                            src={imageUrl}
                            alt={image.description || actualite.titre}
                            className="w-full h-64 object-cover rounded-lg shadow-md hover:shadow-xl transition-shadow cursor-pointer"
                            loading="lazy"
                          />
                          {image.description && (
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 rounded-b-lg">
                              <p className="text-white text-sm">{image.description}</p>
                            </div>
                          )}
                        </a>
                      );
                    })}
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <span className="text-sm uppercase tracking-wide text-gray-400">
                {(() => {
                  const text = getText('news.share');
                  return text === 'news.share' ? 'Partager' : text;
                })()}
              </span>
              <div className="flex items-center gap-3">
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition"
                  aria-label="Partager sur Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sky-500 text-white hover:bg-sky-600 transition"
                  aria-label="Partager sur Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <button
                  type="button"
                  onClick={() => {
                    if (navigator?.clipboard?.writeText) {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  className="inline-flex items-center justify-center px-4 py-2 rounded-full border border-gray-200 text-gray-700 hover:bg-gray-100 transition text-sm font-medium"
                >
                  {(() => {
                    const text = getText('news.copy_link');
                    return text === 'news.copy_link' ? 'Copier le lien' : text;
                  })()}
                </button>
              </div>
            </div>
          </div>
        </article>

        {otherNews.length > 0 && (
          <section className="mt-16">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-gray-900">
                {(() => {
                  const text = getText('news.related');
                  return text === 'news.related'
                    ? 'Autres actualités'
                    : text;
                })()}
              </h2>
              <Link
                to="/news"
                className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors font-semibold"
              >
                {(() => {
                  const text = getText('news.see_all');
                  return text === 'news.see_all' ? 'Voir toutes les actualités' : text;
                })()}
                <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
              </Link>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {otherNews.map((item) => {
                const cardImage = resolveImageUrl(item.image_url_final);
                return (
                  <Link
                    key={item.id}
                    to={`/news/${item.slug}`}
                    className="bg-white rounded-2xl shadow-sm border border-gray-100 hover:border-blue-200 transition overflow-hidden"
                  >
                    {cardImage && (
                      <img
                        src={cardImage}
                        alt={item.titre}
                        className="w-full h-40 object-cover"
                        loading="lazy"
                      />
                    )}
                    <div className="p-5">
                      <div className="text-xs uppercase tracking-wide text-blue-600 font-semibold mb-2">
                        {formatDate(item.date_publication)}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                        {item.titre}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

